import React from "react";
import { Stack, TextField, Typography, FormControlLabel, Switch } from "@mui/material";

const normalizeArray = (a) => (Array.isArray(a) ? a : []);

const QuestionFormDropdown = ({ value, onChange }) => {
    const v = value || {};
    const parts = normalizeArray(v.dropdownQuestionParts);
    const options = normalizeArray(v.options);

    const correctAnswers =
        v.correctAnswers && typeof v.correctAnswers === "object" ? v.correctAnswers : {};

    function setPart(idx, nextVal) {
        const next = parts.map((p, i) => (i === idx ? nextVal : p));
        onChange({ ...v, dropdownQuestionParts: next });
    }

    function addTextPart() {
        onChange({ ...v, dropdownQuestionParts: [...parts, ""] });
    }

    function addDropdownPart() {
        onChange({
            ...v,
            dropdownQuestionParts: [...parts, { id: `dropdown${parts.length + 1}`, text: "" }],
        });
    }

    function removePart(idx) {
        const next = parts.filter((_, i) => i !== idx);
        onChange({ ...v, dropdownQuestionParts: next });
    }

    function setOption(idx, patch) {
        const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
        onChange({ ...v, options: next });
    }

    function addOption() {
        onChange({ ...v, options: [...options, { id: "", text: "" }] });
    }

    function removeOption(idx) {
        onChange({ ...v, options: options.filter((_, i) => i !== idx) });
    }

    function setCorrectAnswer(dropdownId, answerId) {
        onChange({
            ...v,
            correctAnswers: { ...correctAnswers, [dropdownId]: answerId },
        });
    }

    const dropdownIds = parts
        .filter((p) => p && typeof p === "object" && p.id)
        .map((p) => String(p.id));

    const dropdownAmountValue = (v.dropdownAmount ?? dropdownIds.length) || 0;

    return (
        <Stack spacing={2}>
            <TextField
                label="subtitle"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                fullWidth
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={Boolean(v.showAnswerOptions)}
                        onChange={(e) => onChange({ ...v, showAnswerOptions: e.target.checked })}
                    />
                }
                label="showAnswerOptions"
            />

            <TextField
                label="dropdownAmount"
                type="number"
                value={dropdownAmountValue}
                onChange={(e) =>
                    onChange({ ...v, dropdownAmount: Number(e.target.value) || 0 })
                }
                fullWidth
            />

            <Typography variant="subtitle2">dropdownQuestionParts</Typography>

            {parts.map((p, idx) => {
                const isObj = p && typeof p === "object";
                return (
                    <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                        {isObj ? (
                            <>
                                <TextField
                                    label="dropdown id"
                                    value={p.id || ""}
                                    onChange={(e) => setPart(idx, { ...p, id: e.target.value })}
                                    sx={{ width: { sm: 180 } }}
                                />
                                <TextField
                                    label="text"
                                    value={p.text || ""}
                                    onChange={(e) => setPart(idx, { ...p, text: e.target.value })}
                                    fullWidth
                                />
                            </>
                        ) : (
                            <TextField
                                label="text/html"
                                value={String(p)}
                                onChange={(e) => setPart(idx, e.target.value)}
                                fullWidth
                            />
                        )}
                        <TextField
                            value="✕"
                            onClick={() => removePart(idx)}
                            sx={{ width: { sm: 60 }, "& input": { cursor: "pointer", textAlign: "center" } }}
                            inputProps={{ readOnly: true }}
                        />
                    </Stack>
                );
            })}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                    value="+ text part"
                    onClick={addTextPart}
                    inputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ "& input": { cursor: "pointer" } }}
                />
                <TextField
                    value="+ dropdown part"
                    onClick={addDropdownPart}
                    inputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ "& input": { cursor: "pointer" } }}
                />
            </Stack>

            <Typography variant="subtitle2">options</Typography>

            {options.map((o, idx) => (
                <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                        label="id"
                        value={o.id || ""}
                        onChange={(e) => setOption(idx, { id: e.target.value })}
                        sx={{ width: { sm: 120 } }}
                    />
                    <TextField
                        label="text"
                        value={o.text || ""}
                        onChange={(e) => setOption(idx, { text: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        value="✕"
                        onClick={() => removeOption(idx)}
                        sx={{ width: { sm: 60 }, "& input": { cursor: "pointer", textAlign: "center" } }}
                        inputProps={{ readOnly: true }}
                    />
                </Stack>
            ))}

            <TextField
                value="+ add option"
                onClick={addOption}
                inputProps={{ readOnly: true }}
                sx={{ "& input": { cursor: "pointer" } }}
            />

            <Typography variant="subtitle2">correctAnswers</Typography>

            {dropdownIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Додай dropdown parts з id (наприклад dropdown1), тоді зʼявиться мапа відповідей.
                </Typography>
            ) : (
                dropdownIds.map((did) => (
                    <TextField
                        key={did}
                        label={did}
                        value={correctAnswers[did] || ""}
                        onChange={(e) => setCorrectAnswer(did, e.target.value)}
                        placeholder="A / Б / В / Г ..."
                        fullWidth
                    />
                ))
            )}
        </Stack>
    );
};

export default QuestionFormDropdown;
